import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { WifiOff, RotateCw, Save } from 'lucide-react';

/**
 * Lightweight prompt shown when a save attempt fails because the device is
 * offline. The form keeps its in-memory state, the localStorage draft is
 * already up-to-date thanks to `useFormDraft`, so the user can either retry
 * or close and come back later.
 */
export default function OfflineSavePrompt({ open, onClose, onRetry, retrying = false }) {
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !retrying) onClose(); }}>
            <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="offline-save-dialog">
                <DialogTitle className="sr-only">Connexion perdue</DialogTitle>
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <WifiOff className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-[#1B365D]">Connexion perdue</h2>
                            <p className="text-sm text-gray-500">Vos données n'ont pas pu être envoyées.</p>
                        </div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2">
                        <Save className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-emerald-700">
                            Pas de panique : votre saisie est <strong>automatiquement sauvegardée</strong> sur cet appareil.
                            Vous pouvez fermer cette page et revenir plus tard, ou réessayer dès que le réseau est de retour.
                        </p>
                    </div>
                </div>
                <div className="flex justify-end gap-2 px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <Button variant="outline" onClick={onClose} disabled={retrying} data-testid="offline-save-close">
                        Fermer
                    </Button>
                    <Button
                        className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                        onClick={onRetry}
                        disabled={retrying}
                        data-testid="offline-save-retry"
                    >
                        <RotateCw className={`w-4 h-4 mr-1.5 ${retrying ? 'animate-spin' : ''}`} />
                        {retrying ? 'Nouvelle tentative…' : 'Réessayer'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function isNetworkError(err) {
    if (!err) return false;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
    // axios: response missing means request failed before reaching the server
    if (err.response === undefined) {
        const code = err.code || '';
        const msg = String(err.message || '').toLowerCase();
        return (
            code === 'ERR_NETWORK' ||
            code === 'ECONNABORTED' ||
            code === 'ETIMEDOUT' ||
            msg.includes('network') ||
            msg.includes('failed to fetch') ||
            msg.includes('timeout')
        );
    }
    return false;
}

/**
 * useResilientSave : helper that calls `saveFn`, and if it fails because of
 * a network error, opens the offline prompt so the user can retry.
 */
export function useResilientSave() {
    const [open, setOpen] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [pendingFn, setPendingFn] = useState(null);

    const trySave = useCallback(async (saveFn) => {
        try {
            const res = await saveFn();
            return { ok: true, res };
        } catch (err) {
            if (isNetworkError(err)) {
                setPendingFn(() => saveFn);
                setOpen(true);
                return { ok: false, err, offline: true };
            }
            return { ok: false, err, offline: false };
        }
    }, []);

    const retry = useCallback(async () => {
        if (!pendingFn) { setOpen(false); return; }
        setRetrying(true);
        try {
            await pendingFn();
            setOpen(false);
            setPendingFn(null);
        } catch (err) {
            if (!isNetworkError(err)) {
                // Not a network problem anymore → close the modal and let the
                // calling code surface the actual server error.
                setOpen(false);
                setPendingFn(null);
            }
        } finally {
            setRetrying(false);
        }
    }, [pendingFn]);

    const close = useCallback(() => { setOpen(false); setPendingFn(null); }, []);

    return {
        trySave,
        Modal: <OfflineSavePrompt open={open} onClose={close} onRetry={retry} retrying={retrying} />,
    };
}
